/**
 * Reload the page when a visitor saves a new cookie-consent choice.
 *
 * Several third-party tags and site behaviours branch on Osano consent at
 * load time — HeadScripts.js (tag injection), MarketingStore.js (referrer +
 * marketing attribution), useCheckGatedCookie.js (gated-content cookies),
 * MarketoForm/submitForm.js. None of them re-evaluate on their own, so a
 * visitor who accepts after the page has rendered stays on the pre-consent
 * version of the site until they navigate.
 *
 * A hard reload re-runs all of them at once. It is deliberately blunt: the
 * alternative is wiring per-tag activation, which is gentler on the visitor
 * (no lost scroll position or form state) but has to be maintained tag by
 * tag. See src/utils/fullstory/consent.ts for that style of bridge.
 *
 * Loop safety is the whole game here, because Osano fires 'consent-saved'
 * in two situations that look identical to a listener:
 *
 *   1. the visitor just saved a choice          -> reload is correct
 *   2. a returning visitor's stored choice is
 *      replayed on the next animation frame
 *      when we subscribe                        -> reload would loop forever
 *
 * Two guards handle it. The baseline is taken inside 'initialized' — Osano
 * only emits that once its store is ready, so the replay compares equal to
 * it — and a reload additionally requires that the visitor has actually
 * touched the page, which a replay never involves.
 */

import { OSANO_COMMANDS, onOsanoEvent } from '../utils/fullstory/consent';

// Categories the site actually branches on. A change to any of these means
// the current page was rendered under stale assumptions. ESSENTIAL is
// omitted — it is always accepted.
const RELEVANT_CATEGORIES = ['MARKETING', 'ANALYTICS', 'PERSONALIZATION', 'STORAGE', 'OPT_OUT'];

const readConsent = () => window.Osano?.cm?.getConsent?.();

const snapshot = (consent) => RELEVANT_CATEGORIES.map((key) => `${key}:${consent?.[key] || ''}`).join('|');

// Saving a consent choice always follows a click or a keypress on the
// banner. Osano's replay of a stored choice arrives with no user gesture
// behind it at all, which separates the two cleanly — no timer, no
// persisted state, and no way for it to suppress a genuine save.
let sawUserGesture = false;

const watchForUserGesture = () => {
  const note = () => {
    sawUserGesture = true;
  };
  document.addEventListener('pointerdown', note, { capture: true, once: true, passive: true });
  document.addEventListener('keydown', note, { capture: true, once: true, passive: true });
};

/**
 * Bind the reload. Call once per full page load (gatsby-browser.js
 * onInitialClientRender) — the listener survives client-side navigation.
 */
const initOsanoConsentReload = () => {
  if (typeof window === 'undefined') return;

  watchForUserGesture();

  onOsanoEvent(OSANO_COMMANDS.ON_INITIALIZED, (initialConsent) => {
    // The consent this page is effectively rendered under.
    let applied = snapshot(initialConsent || readConsent());

    onOsanoEvent(OSANO_COMMANDS.ON_CONSENT_SAVED, (consent) => {
      const next = snapshot(consent || readConsent());
      const changed = next !== applied;
      applied = next;

      // Guard 1: the drawer can be saved without touching anything we
      // branch on.
      if (!changed) return;

      // Guard 2: no gesture means this is Osano replaying a stored choice
      // on subscribe, not a visitor saving one. Reloading for that would
      // repeat on every page load.
      if (!sawUserGesture) return;

      window.location.reload();
    });
  });
};

export default initOsanoConsentReload;
